import React, { useRef, useState } from "react";
import { Upload, FileBox, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const PROCESSES = [
  { id: "FDM", label: "FDM" },
  { id: "BATCH", label: "Batch / Production" },
  { id: "OTHER", label: "Other" },
];

const initial = {
  name: "",
  email: "",
  company: "",
  process: "FDM",
  material: "",
  quantity: 1,
  deadline: "",
  description: "",
};

export const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      toast.error("MISSING FIELDS", { description: "Name, email and project description are required." });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("process", form.process);
      fd.append("description", form.description);
      if (form.company) fd.append("company", form.company);
      if (form.material) fd.append("material", form.material);
      if (form.deadline) fd.append("deadline", form.deadline);
      fd.append("quantity", String(Number(form.quantity) || 1));
      if (file) fd.append("file", file);

      await api.post("/quote-requests", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("QUOTE RECEIVED", {
        description: "We'll review your job and reply within 24 hours.",
      });
      setForm(initial);
      setFile(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Submission failed";
      toast.error("SUBMISSION FAILED", { description: String(msg) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative py-24 md:py-32 border-b border-[#2A2A2A] bg-[#0F0F0F]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <p className="overline mb-6">// 05 — Get a quote</p>
          <h2
            className="font-display uppercase leading-[0.9] text-white mb-8"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}
            data-testid="quote-headline"
          >
            Send the<br />
            <span className="text-[#FF5722]">file.</span>
          </h2>
          <p className="font-body text-white/70 max-w-sm mb-8">
            Drop your model + a few details. You'll get a hand-reviewed quote (no AI auto-pricing) within
            one business day.
          </p>
          <ul className="space-y-3 border-t border-[#2A2A2A] pt-6">
            {[
              "STL · OBJ · STEP · 3MF",
              "Up to 200MB per upload",
              "NDA on request",
            ].map((s) => (
              <li key={s} className="font-mono-ui text-[11px] tracking-[0.2em] uppercase text-white/60 flex items-center gap-3">
                <span className="w-2 h-2 bg-[#FF5722] inline-block" /> {s}
              </li>
            ))}
          </ul>
        </div>

        <form
          onSubmit={submit}
          className="md:col-span-8 bg-[#0A0A0A] border border-[#2A2A2A] p-6 md:p-10"
          data-testid="quote-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Name *</label>
              <input
                className="toad-input"
                placeholder="Jane Doe"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="quote-input-name"
                required
              />
            </div>
            <div>
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Email *</label>
              <input
                type="email"
                className="toad-input"
                placeholder="jane@studio.io"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                data-testid="quote-input-email"
                required
              />
            </div>
            <div>
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Company</label>
              <input
                className="toad-input"
                placeholder="Optional"
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                data-testid="quote-input-company"
              />
            </div>
            <div>
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Deadline</label>
              <input
                className="toad-input"
                placeholder="e.g. 2 weeks"
                value={form.deadline}
                onChange={(e) => set("deadline", e.target.value)}
                data-testid="quote-input-deadline"
              />
            </div>

            <div className="md:col-span-2">
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-3 block">Process *</label>
              <div className="flex flex-wrap gap-2">
                {PROCESSES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => set("process", p.id)}
                    data-testid={`quote-process-${p.id}`}
                    className={`font-mono-ui text-[11px] tracking-[0.25em] uppercase px-4 py-2.5 border-2 transition-colors ${
                      form.process === p.id
                        ? "bg-[#FF5722] border-[#FF5722] text-white"
                        : "bg-transparent border-[#2A2A2A] text-white/70 hover:border-white"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Material</label>
              <input
                className="toad-input"
                placeholder="PLA, PETG, ABS, ASA, TPU..."
                value={form.material}
                onChange={(e) => set("material", e.target.value)}
                data-testid="quote-input-material"
              />
            </div>
            <div>
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Quantity</label>
              <input
                type="number"
                min={1}
                className="toad-input"
                value={form.quantity}
                onChange={(e) => set("quantity", e.target.value)}
                data-testid="quote-input-quantity"
              />
            </div>

            <div className="md:col-span-2">
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2 block">Project description *</label>
              <textarea
                className="toad-input"
                rows={4}
                placeholder="Tell us about the part — usage, finish, tolerances..."
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                data-testid="quote-input-description"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/50 mb-3 block">3D File</label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-testid="quote-dropzone"
                className={`cursor-pointer border-2 border-dashed p-8 md:p-12 flex flex-col items-center justify-center text-center transition-colors ${
                  dragOver ? "border-[#FF5722] bg-[#FF5722]/5" : "border-[#2A2A2A] hover:border-[#FF5722]"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".stl,.obj,.step,.stp,.3mf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="quote-input-file"
                />
                {file ? (
                  <div className="flex items-center gap-3">
                    <FileBox size={32} className="text-[#FF5722]" />
                    <div className="text-left">
                      <p className="font-mono-ui text-xs tracking-[0.2em] text-white">{file.name}</p>
                      <p className="font-mono-ui text-[10px] tracking-[0.2em] text-white/50">
                        {(file.size / 1024).toFixed(1)} KB · click to replace
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload size={32} className="text-white/60 mb-3" />
                    <p className="font-mono-ui text-xs tracking-[0.25em] uppercase text-white/80">
                      Drop file or click to browse
                    </p>
                    <p className="font-mono-ui text-[10px] tracking-[0.25em] text-white/40 mt-1">
                      .STL · .OBJ · .STEP · .3MF
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-t border-[#2A2A2A] pt-6">
            <p className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-white/40">
              We reply within 24 hours · No spam, ever
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="quote-submit"
              className="inline-flex items-center justify-center gap-3 bg-[#FF5722] hover:bg-white hover:text-black text-white font-mono-ui text-xs tracking-[0.25em] uppercase font-bold px-8 py-4 transition-colors disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  Submit Request <ArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default QuoteForm;
